import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Portal,
} from '@mui/material';
import {
  ShoppingBag,
  X,
  Search,
  ChevronDown,
  ChevronUp,
  UserPlus,
  Check,
  Pencil,
  LoaderCircle,
  UserSearch,
  CircleAlert,
} from 'lucide-react';
import { ClienteAutocompleteItem, CreateBolsaDTO } from '../types';
import { NuevoClienteDrawer } from '../../clientes/components/NuevoClienteDrawer';
import { clienteService } from '../../clientes/services/clienteService';

type DropdownState = 'idle' | 'loading' | 'results' | 'empty' | 'error';

interface NuevaBolsaModalProps {
  open: boolean;
  onClose: () => void;
  onCreateBolsa: (dto: CreateBolsaDTO) => Promise<void>;
}

export function NuevaBolsaModal({ open, onClose, onCreateBolsa }: NuevaBolsaModalProps) {
  const [searchText, setSearchText] = useState('');
  const [opciones, setOpciones] = useState<ClienteAutocompleteItem[]>([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<ClienteAutocompleteItem | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownState, setDropdownState] = useState<DropdownState>('idle');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showCrearClienteDrawer, setShowCrearClienteDrawer] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resetear estado al cerrar
  useEffect(() => {
    if (!open) {
      setSearchText('');
      setOpciones([]);
      setClienteSeleccionado(null);
      setDropdownOpen(false);
      setDropdownState('idle');
      setSubmitting(false);
      setSubmitError(null);
      setShowCrearClienteDrawer(false);
    }
  }, [open]);

  const buscarClientes = useCallback(async (texto: string) => {
    if (!texto.trim()) {
      setOpciones([]);
      setDropdownOpen(false);
      setDropdownState('idle');
      return;
    }
    setDropdownState('loading');
    setDropdownOpen(true);
    try {
      const data = await clienteService.listar({ busqueda: texto, limite: 20, pagina: 1 });
      const items: ClienteAutocompleteItem[] = (data.data || []).map((c: { id: string; nit: string | null; razonSocial: string; ciudad?: string }) => ({
        id: c.id,
        nit: c.nit,
        razonSocial: c.razonSocial,
        ciudad: c.ciudad,
      }));
      setOpciones(items);
      setDropdownState(items.length > 0 ? 'results' : 'empty');
    } catch {
      setDropdownState('error');
    }
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchText(val);
    setClienteSeleccionado(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buscarClientes(val), 300);
  };

  const handleSelectCliente = (item: ClienteAutocompleteItem) => {
    setClienteSeleccionado(item);
    setDropdownOpen(false);
    setDropdownState('idle');
    setSearchText(item.razonSocial);
  };

  const handleCambiar = () => {
    setClienteSeleccionado(null);
    setSearchText('');
    setOpciones([]);
    setDropdownOpen(false);
    setDropdownState('idle');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleCreateBolsa = async () => {
    if (!clienteSeleccionado || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onCreateBolsa({
        nombre: clienteSeleccionado.razonSocial,
        cliente_id: clienteSeleccionado.id,
        cliente_nit: clienteSeleccionado.nit ?? undefined,
        cliente_razon_social: clienteSeleccionado.razonSocial,
      });
      onClose();
    } catch {
      setSubmitError('No fue posible crear la bolsa. Intenta nuevamente.');
      setSubmitting(false);
    }
  };

  const handleInputFocus = () => {
    if (searchText.trim() && opciones.length > 0) {
      setDropdownOpen(true);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && dropdownOpen) {
      e.stopPropagation();
      setDropdownOpen(false);
    }
  };

  // ─── Animación ───────────────────────────────────────────────────────────
  const paperSx = {
    width: 'min(720px, calc(100vw - 48px))',
    maxHeight: 'calc(100vh - 48px)',
    borderRadius: '16px',
    boxShadow: '0 24px 70px rgba(31, 18, 40, 0.24)',
    border: '1px solid rgba(116, 48, 95, 0.08)',
    m: 0,
    overflow: 'visible',
    '@keyframes fadeScaleIn': {
      from: { opacity: 0, transform: 'scale(0.96)' },
      to:   { opacity: 1, transform: 'scale(1)' },
    },
    animation: 'fadeScaleIn 180ms ease-out',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  };

  const disabled = !clienteSeleccionado || submitting;

  return (
    <>
      <Dialog
        open={open && !showCrearClienteDrawer}
        onClose={onClose}
        keepMounted
        aria-labelledby="new-bag-title"
        aria-describedby="new-bag-subtitle"
        PaperProps={{ sx: paperSx }}
        slotProps={{
          backdrop: {
            sx: { backdropFilter: 'blur(3px)', background: 'rgba(25, 16, 28, 0.52)' },
          },
        }}
        sx={{ zIndex: 1200 }}
      >
        <DialogContent sx={{ p: '32px', overflow: 'visible' }}>
          {/* ── HEADER ─────────────────────────────────────────────── */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px', mb: '24px' }}>
            {/* Icono ShoppingBag */}
            <Box
              sx={{
                width: 44, height: 44, flexShrink: 0,
                background: '#F6EAF2', borderRadius: '10px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <ShoppingBag size={24} color="#74305F" strokeWidth={2} />
            </Box>

            {/* Título + subtítulo */}
            <Box sx={{ flex: 1 }}>
              <Typography
                id="new-bag-title"
                sx={{ fontSize: '26px', fontWeight: 700, lineHeight: 1.2, color: '#241B25' }}
              >
                Nueva bolsa
              </Typography>
              <Typography
                id="new-bag-subtitle"
                sx={{ fontSize: '15px', fontWeight: 400, lineHeight: 1.5, color: '#716A73', mt: '4px' }}
              >
                Selecciona un cliente para iniciar una cotización.
              </Typography>
            </Box>

            {/* Botón cerrar */}
            <IconButton
              onClick={onClose}
              aria-label="Cerrar ventana"
              title="Cerrar"
              sx={{
                width: 40, height: 40, borderRadius: '8px',
                color: '#625A64',
                '&:hover': { background: '#F5F2F5' },
              }}
            >
              <X size={20} />
            </IconButton>
          </Box>

          {/* ── CLIENT SELECTOR ────────────────────────────────────── */}
          <Box sx={{ mb: '24px' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#352C36', mb: '8px' }}>
              Cliente
            </Typography>

            {/* Input con iconos */}
            <Box sx={{ position: 'relative' }}>
              {/* Search icon izquierda */}
              <Box
                sx={{
                  position: 'absolute', left: 14, top: '50%',
                  transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 1,
                  display: 'flex', alignItems: 'center',
                }}
              >
                <Search size={20} color="#776F79" />
              </Box>

              {/* Input */}
              <Box
                component="input"
                ref={inputRef}
                type="text"
                value={searchText}
                onChange={handleSearchChange}
                onFocus={handleInputFocus}
                onKeyDown={handleInputKeyDown}
                placeholder="Buscar por razón social, NIT o ciudad"
                data-testid="nueva-bolsa-search-input"
                sx={{
                  width: '100%',
                  height: '56px',
                  padding: '0 48px 0 46px',
                  fontSize: '16px',
                  color: '#2B232C',
                  background: '#FFFFFF',
                  border: '1px solid #D9D3DA',
                  borderRadius: '10px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  '&::placeholder': { color: '#8A828C' },
                  '&:focus': {
                    borderColor: '#74305F',
                    boxShadow: '0 0 0 3px rgba(116, 48, 95, 0.14)',
                  },
                }}
              />

              {/* ChevronDown / ChevronUp derecha */}
              <Box
                sx={{
                  position: 'absolute', right: 14, top: '50%',
                  transform: 'translateY(-50%)', pointerEvents: 'none',
                  display: 'flex', alignItems: 'center',
                }}
              >
                {dropdownOpen
                  ? <ChevronUp size={20} color="#776F79" />
                  : <ChevronDown size={20} color="#776F79" />
                }
              </Box>

              {/* ── DROPDOWN ────────────────────────────────── */}
              {dropdownOpen && (
                <Box
                  data-testid="nueva-bolsa-dropdown"
                  sx={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    left: 0, right: 0,
                    zIndex: 10,
                    maxHeight: '280px',
                    overflowY: 'auto',
                    background: '#FFFFFF',
                    border: '1px solid #DED8DF',
                    borderRadius: '10px',
                    boxShadow: '0 12px 32px rgba(39, 27, 42, 0.16)',
                  }}
                >
                  {/* Estado loading */}
                  {dropdownState === 'loading' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '16px', justifyContent: 'center' }}>
                      <Box
                        sx={{
                          '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
                          animation: 'spin 1s linear infinite',
                          display: 'flex',
                        }}
                      >
                        <LoaderCircle size={18} color="#74305F" />
                      </Box>
                      <Typography sx={{ fontSize: '14px', color: '#7A727C' }}>
                        Buscando clientes...
                      </Typography>
                    </Box>
                  )}

                  {/* Estado empty */}
                  {dropdownState === 'empty' && (
                    <Box sx={{ p: '20px', textAlign: 'center' }}>
                      <UserSearch size={32} color="#B0A8B2" style={{ marginBottom: 8 }} />
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#302731', mb: '4px' }}>
                        No encontramos clientes
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#766E78', mb: '12px' }}>
                        Verifica la búsqueda o crea un cliente nuevo.
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => { setDropdownOpen(false); setShowCrearClienteDrawer(true); }}
                        startIcon={<UserPlus size={15} />}
                        sx={{ color: '#74305F', textTransform: 'none', fontSize: '13px', fontWeight: 600 }}
                      >
                        Crear nuevo cliente
                      </Button>
                    </Box>
                  )}

                  {/* Estado error */}
                  {dropdownState === 'error' && (
                    <Box sx={{ p: '20px', textAlign: 'center' }}>
                      <CircleAlert size={28} color="#B42318" style={{ marginBottom: 8 }} />
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#302731', mb: '12px' }}>
                        No fue posible cargar los clientes
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => buscarClientes(searchText)}
                        sx={{ color: '#74305F', textTransform: 'none', fontSize: '13px', fontWeight: 600 }}
                      >
                        Intentar de nuevo
                      </Button>
                    </Box>
                  )}

                  {/* Estado results */}
                  {dropdownState === 'results' && opciones.map((item) => (
                    <Box
                      key={item.id}
                      data-testid="nueva-bolsa-result-item"
                      onClick={() => handleSelectCliente(item)}
                      sx={{
                        minHeight: '64px',
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        background: clienteSeleccionado?.id === item.id ? '#F6EAF2' : '#FFFFFF',
                        '&:hover': { background: '#FAF5F8' },
                        borderBottom: '1px solid #F0EBF0',
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#302731', lineHeight: 1.3 }}>
                          {item.razonSocial}
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: '#766E78', mt: '2px' }}>
                          NIT {item.nit ?? '—'}{item.ciudad ? ` · ${item.ciudad}` : ''}
                        </Typography>
                      </Box>
                      {clienteSeleccionado?.id === item.id && (
                        <Check size={18} color="#74305F" style={{ flexShrink: 0, marginLeft: 8 }} />
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Helper text */}
            <Typography sx={{ fontSize: '13px', color: '#7A727C', mt: '7px' }}>
              Puedes buscar por nombre, NIT o ciudad.
            </Typography>

            {/* Acción rápida: Crear nuevo cliente */}
            <Button
              data-testid="nueva-bolsa-crear-cliente-btn"
              onClick={() => setShowCrearClienteDrawer(true)}
              startIcon={<UserPlus size={17} />}
              sx={{
                mt: '12px',
                color: '#74305F',
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                p: 0,
                minWidth: 0,
                '&:hover': { background: 'transparent', textDecoration: 'underline' },
              }}
            >
              Crear nuevo cliente
            </Button>
          </Box>

          {/* ── SELECTED CLIENT SUMMARY ────────────────────────────── */}
          {clienteSeleccionado && (
            <Box
              aria-live="polite"
              data-testid="nueva-bolsa-cliente-summary"
              sx={{
                background: '#FBF7FA',
                border: '1px solid #E8DCE4',
                borderRadius: '10px',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: '24px',
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#8A5077', letterSpacing: '0.5px' }}>
                  Cliente seleccionado
                </Typography>
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#302631', mt: '5px' }}>
                  {clienteSeleccionado.razonSocial}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#6F6672', mt: '3px' }}>
                  NIT {clienteSeleccionado.nit ?? '—'}{clienteSeleccionado.ciudad ? ` · ${clienteSeleccionado.ciudad}` : ''}
                </Typography>
              </Box>
              <Button
                data-testid="nueva-bolsa-cambiar-btn"
                onClick={handleCambiar}
                aria-label="Cambiar cliente seleccionado"
                startIcon={<Pencil size={16} />}
                sx={{
                  color: '#74305F',
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'none',
                  flexShrink: 0,
                  ml: '12px',
                  '&:hover': { background: 'transparent', textDecoration: 'underline' },
                }}
              >
                Cambiar
              </Button>
            </Box>
          )}

          {/* ── SUBMIT ERROR ───────────────────────────────────────── */}
          {submitError && (
            <Box
              sx={{
                background: '#FEF3F2',
                border: '1px solid #FECDCA',
                borderRadius: '8px',
                p: '12px 16px',
                mb: '16px',
              }}
            >
              <Typography sx={{ fontSize: '13px', color: '#B42318' }}>
                {submitError}
              </Typography>
            </Box>
          )}

          {/* ── FOOTER ACTIONS ─────────────────────────────────────── */}
          <Box
            sx={{
              borderTop: '1px solid #EEE9EE',
              pt: '20px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              flexDirection: 'row',
              '@media (max-width: 600px)': {
                flexDirection: 'column-reverse',
                gap: '10px',
              },
            }}
          >
            {/* Cancelar */}
            <Button
              data-testid="nueva-bolsa-cancelar-btn"
              onClick={onClose}
              sx={{
                height: '44px',
                padding: '0 20px',
                background: '#FFFFFF',
                color: '#74305F',
                border: '1px solid #DCCFD8',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { background: '#FAF5F8' },
                '@media (max-width: 600px)': { width: '100%' },
              }}
            >
              Cancelar
            </Button>

            {/* Crear bolsa */}
            <Button
              data-testid="nueva-bolsa-crear-btn"
              onClick={handleCreateBolsa}
              disabled={disabled}
              startIcon={submitting
                ? <CircularProgress size={16} sx={{ color: disabled ? '#A6A2A7' : '#FFFFFF' }} />
                : <ShoppingBag size={17} />
              }
              sx={{
                height: '44px',
                padding: '0 22px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'none',
                border: '1px solid',
                ...(disabled
                  ? {
                      background: '#E2E0E2',
                      color: '#A6A2A7',
                      borderColor: '#E2E0E2',
                      '& .lucide': { color: '#A6A2A7' },
                    }
                  : {
                      background: '#74305F',
                      color: '#FFFFFF',
                      borderColor: '#74305F',
                      '&:hover': { background: '#62274F', borderColor: '#62274F' },
                      '&:active': { background: '#522042', borderColor: '#522042' },
                    }
                ),
                '@media (max-width: 600px)': { width: '100%' },
              }}
            >
              {submitting ? 'Creando bolsa...' : 'Crear bolsa'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── NUEVO CLIENTE DRAWER ───────────────────────────────────── */}
      {showCrearClienteDrawer && (
        <Portal>
          <NuevoClienteDrawer
            open={showCrearClienteDrawer}
            onClose={() => setShowCrearClienteDrawer(false)}
            onCreado={(cliente) => {
              if (cliente) {
                setClienteSeleccionado({
                  id: cliente.id,
                  nit: cliente.nit,
                  razonSocial: cliente.razonSocial,
                  ciudad: cliente.ciudad,
                });
                setSearchText(cliente.razonSocial);
              }
              setShowCrearClienteDrawer(false);
            }}
          />
        </Portal>
      )}
    </>
  );
}
